import * as CORE from 'app/model/CORE';
export interface ConfiguredAirspace {
    airspace?: CORE.AirspaceRef;
    sectorOpenings?: Array<AtcSectorOpening>;
}
export interface AtcSectorOpening {
    airspaces?: Array<CORE.AirspaceRef>;
    configId?: string;
    timePeriod?: CORE.TimePeriod;
    trafficVolumes?: Array<string>;
}
export interface ExtendedMonitoredTv extends CORE.MonitoredTv {
    activePeriods?: Array<CORE.TimePeriod>;
    elementary?: boolean;
    monitorable?: boolean;
    trafficCounts?: TrafficVolumeCounts;
    tvRefLocation?: CORE.TvRefLocation;
}
export interface MonitoredMeasure {
    delay?: number;
    measure?: CORE.MeasureDto;
    measureLoad?: Array<CORE.MonitoredLoadValue>;
    warnings?: Array<MeasureWarning>;
}
export interface MonitoredMcdmMeasure extends McdmMeasure {
    hourlyCapacity?: Array<CORE.CapacityValue>;
    linkedRegulation?: CORE.RegulationDto;
}
export interface McdmMeasure {
    mcdmMeasureTopic?: CORE.McdmMeasureTopic;
    measure?: CORE.MeasureDto;
}
export interface CoordinationSummary {
    id?: string;
    name?: string;
    owner?: string;
    solutions?: Array<Solution>;
    state?: CoordinationState;
    status?: { [key: string]: { [key: string]: SolutionState } };
    targetTime?: Date;
    unreadMessage?: boolean;
}
export enum CoordinationState {
    ONGOING = 'ONGOING',
    APPLIED = 'APPLIED',
    CANCELLED = 'CANCELLED',
}
export enum SolutionState {
    NO_RESPONSE = 'NO_RESPONSE',
    ACKNOWLEDGED = 'ACKNOWLEDGED',
    APPROVED = 'APPROVED',
    REJECTED = 'REJECTED',
    TIMED_OUT = 'TIMED_OUT',
}
export interface UpdateCoordinationStateRequest {
    anuId?: string;
    id?: string;
    solutionName?: string;
    state?: SolutionState;
}
export interface Coordination {
    id?: string;
    lastAccessTime?: { [key: string]: Date };
    name?: string;
    owner?: string;
    solutions?: Array<DetailedSolution>;
    state?: CoordinationState;
    status?: { [key: string]: { [key: string]: SolutionState } };
    targetTime?: Date;
    version?: number;
}
export interface CreateCoordinationRequest {
    comment?: string;
    coordination?: Coordination;
}
export interface DelaySummary {
    averageDelayMs?: number;
    delayByLoadClassification?: Array<DelayByLoadClassification>;
    delayByLocationSummary?: Array<DelayByLocationSummary>;
    totalDelayMs?: number;
}
export interface Sol49FlightAirspaceCrossing extends CORE.FlightAirspaceCrossing {
    countType?: CORE.CountType;
    loadValue?: CORE.MonitoredLoadValue;
    tvId?: string;
}
export interface FilteredFlightExtraData {
    flightId?: CORE.FlightId;
    timeAtReferenceLocationEntry?: CORE.TimeAndTrafficType;
    timeAtReferenceLocationExit?: CORE.TimeAndTrafficType;
    tvsOccupancyPeriod?: { [key: string]: CORE.TimePeriod };
}
export interface GetFlightListImpactedByConvectionRequest {
    datasetDto?: CORE.DatasetDto;
    display?: CORE.CountType;
    fields?: Array<CORE.FlightFieldType>;
    flightLevel?: CORE.FlightLevelBand;
    timePeriod?: CORE.TimePeriod;
    tvId?: string;
    tvRefLocAirspaceId?: string;
}
export class Sol49FlightFieldType implements CORE.FlightFieldType {
    public static readonly METSAFE_CONVECTION_IMPACT_LEVEL = [
        'eurocontrol.npp.sol49.shared.dto.flight.Sol49FlightFieldType',
        'METSAFE_CONVECTION_IMPACT_LEVEL',
    ];
    public static readonly DWD_CONVECTION_IMPACT_LEVEL = [
        'eurocontrol.npp.sol49.shared.dto.flight.Sol49FlightFieldType',
        'DWD_CONVECTION_IMPACT_LEVEL',
    ];
    public static readonly DWD_ICONV_CONVECTION_IMPACT_LEVEL = [
        'eurocontrol.npp.sol49.shared.dto.flight.Sol49FlightFieldType',
        'DWD_ICONV_CONVECTION_IMPACT_LEVEL',
    ];
}
export interface Sol49FlightDto extends CORE.FlightDto {
    convectionImpactLevel?: number;
    currentProfileAirspacePoints?: Array<Sol49FlightAirspaceCrossing>;
    numberOverloadTvs?: number;
}
export const SOL49_FLIGHT_DTO_CLAZZ_VALUE = 'eurocontrol.npp.sol49.shared.dto.flight.Sol49FlightDto';
export interface TrafficVolumeMeteoForecast extends MeteoForecast {
    airspaceRef?: CORE.AirspaceRef;
}
export interface MeteoForecast {
    flightLevelBand?: CORE.FlightLevelBand;
    id?: string;
    lastUpdate?: Date;
    level?: number;
    validityPeriod?: CORE.TimePeriod;
}
export enum ConvectionFraction {
    ISOL = 'ISOL',
    CLST = 'CLST',
    WSPR = 'WSPR',
}
export enum ConvectionSource {
    METSAFE = 'METSAFE',
    DWD = 'DWD',
    DWD_ICONV = 'DWD_ICONV',
}
export interface NetworkMapConvectionRequest {
    convectionSource?: ConvectionSource;
    minLevel?: number;
    predictionDay?: Date;
}
export interface TvMeteoAlert {
    impactLevel?: number;
    lastUpdate?: Date;
    meteoLocation?: CORE.AirspaceSummary;
    timePeriod?: CORE.TimePeriod;
    tvName?: string;
}
export enum Likeliness {
    LESS_LIKELY = 'LESS_LIKELY',
    LIKELY = 'LIKELY',
    VERY_LIKELY = 'VERY_LIKELY',
}
export interface NetworkMapConvection {
    airspaceDefinition?: { [key: string]: CORE.Airspace };
    networkForecast?: NetworkMeteoForecast;
}
export interface NetworkMeteoForecast {
    forecastPerTvName?: { [key: string]: TrafficVolumeMeteoForecast };
    forecastValidity?: CORE.TimePeriod;
    lastUpdate?: Date;
    polygonMeteoForecasts?: Array<PolygonMeteoForecast>;
}
export interface PolygonMeteoForecast extends MeteoForecast {
    convectionFraction?: ConvectionFraction;
    impactedTvs?: { [key: string]: CORE.AirspaceRef };
    likeliness?: Likeliness;
    polygon?: Array<CORE.LatLonPoint>;
    radRestrictions?: RadRestrictions;
}
export interface MonitoredRegulationOutlook {
    measure?: Array<MonitoredMeasure>;
    regulationOutlook?: RegulationOutlook;
    tvs?: Array<string>;
}
export interface RegulationOutlook_ReasonAndPrediction {
    prediction?: number;
    reason?: CORE.RegulationReasonType;
}
export interface RegulationOutlook {
    aerodrome?: CORE.Aerodrome;
    aerodromeId?: string;
    date?: Date;
    hourlyPredictions?: Array<number>;
    lastUpdateTs?: Date;
    prediction?: number;
    reasons?: Array<RegulationOutlook_ReasonAndPrediction>;
}
export interface DelayByLoadClassification {
    loadClassification?: CORE.LoadClassification;
    nbDelayedFlights?: number;
}
export interface DelayByLocationSummary {
    displayText?: string;
    nbRegulatedFlights?: number;
    refLocation?: CORE.TvRefLocation;
    totalDelayMs?: number;
}
export interface Solution {
    measures?: Array<CORE.MeasureDto>;
    name?: string;
    tvMatchingKind?: CORE.ScenarioTvMatchingKind;
    tvs?: Array<string>;
    type?: SolutionType;
}
export enum SolutionType {
    INITIAL = 'INITIAL',
    ALTERNATIVE = 'ALTERNATIVE',
    CUSTOM = 'CUSTOM',
}
export interface SolutionImpactSummary {
    nbFlightsConcerned?: number;
    nbFlightsDelayed?: number;
    nbFlightsRegulated?: number;
    nbFlightsRerouted?: number;
    networkCost?: number;
    networkDelayInMs?: number;
    solutionCost?: number;
    solutionDelayInMs?: number;
    validity?: Date;
}
export interface MeasureImpact {
    clazz?: string;
    fmpUnit?: string;
}
export const MEASURE_IMPACT_CLAZZ_VALUE = 'eurocontrol.npp.sol49.shared.dto.solver.MeasureImpact';
export enum TvImpactTypeEnum {
    SELECTION = 'SELECTION',
    NIA_IMPACTED = 'NIA_IMPACTED',
    NIA_IMPACTED_EXTRA = 'NIA_IMPACTED_EXTRA',
}
export interface NiaMeasureImpact extends MeasureImpact {
    applicabilityPeriod?: CORE.TimePeriod;
    delay?: number;
    flightLevelBand?: CORE.FlightLevelBand;
    load?: Array<CORE.MonitoredLoadValue>;
    regulId?: string;
    state?: CORE.RegulationState;
    tvId?: string;
}
export const NIA_MEASURE_IMPACT_CLAZZ_VALUE = 'eurocontrol.npp.sol49.shared.dto.solver.NiaMeasureImpact';
export interface ReroutedFlightDto {
    aircraftType?: string;
    delayInMs?: number;
    deltas?: CORE.ReroutedFlightDeltas;
    flight?: CORE.FlightDto;
    flightId?: CORE.FlightId;
    mostPenalisingRegulation?: string;
    reason?: string;
    reroutingApplied?: boolean;
}
export interface ReroutingImpact extends MeasureImpact {
    reroutedFlights?: Array<ReroutedFlightDto>;
}
export const REROUTING_IMPACT_CLAZZ_VALUE = 'eurocontrol.npp.sol49.shared.dto.solver.ReroutingImpact';
export interface DetailedSolution extends Solution {
    impactSummary?: SolutionImpactSummary;
    solutionImpact?: SolutionImpact;
}
export interface SolutionImpact {
    deltaRegulations?: Array<CORE.DeltaAtfcmSituationRegulationDto>;
    deltaTvImpacts?: Array<NIATvImpact>;
    measureImpacts?: { [key: string]: MeasureImpact };
    tvImpact?: Array<TvImpact>;
}
export interface TvImpact {
    entryCountsComparison?: CORE.TrafficCountsAndComparisonDto;
    monitoredTv?: ExtendedMonitoredTv;
    monitoredTvBefore?: ExtendedMonitoredTv;
    occupancyCountsComparison?: CORE.TrafficCountsAndComparisonDto;
    timePeriod?: CORE.TimePeriod;
    trafficCountsDto?: CORE.TrafficCountsDto;
    tvId?: string;
    type?: TvImpactTypeEnum;
}
export interface RegulationImpact extends MeasureImpact {
    entryCountsComparison?: CORE.TrafficCountsAndComparisonDto;
    flightList?: Array<Sol49FlightDto>;
    occupancyCountsComparison?: CORE.TrafficCountsAndComparisonDto;
    slotList?: CORE.SlotList;
}
export const REGULATION_IMPACT_CLAZZ_VALUE = 'eurocontrol.npp.sol49.shared.dto.solver.RegulationImpact';
export interface MeasureWarning {
    clazz?: string;
}
export const MEASURE_WARNING_CLAZZ_VALUE = 'eurocontrol.npp.sol49.shared.dto.measure.MeasureWarning';
export interface BunchingWarning extends MeasureWarning {
    bunchingTime?: Date;
    nbFlights?: number;
}
export const BUNCHING_WARNING_CLAZZ_VALUE = 'eurocontrol.npp.sol49.shared.dto.measure.BunchingWarning';
export interface TrafficVolumeCounts {
    entryCounts?: TimeRangeCounts;
    flights?: Array<CORE.Pair<CORE.FlightId, CORE.TimePeriod>>;
    hourlyEntryCapacity?: Array<CORE.CapacityValue>;
    occupancyCounts?: Array<TimeRangeCounts>;
    otmvDefns?: { [key: string]: Array<CORE.Otmv> };
    tvId?: string;
    tvSetId?: string;
}
export interface TimeRangeCounts {
    flightModelCounts?: CORE.FlightModelCounts;
    timeRange?: CORE.TimeRange;
}
export interface DashboardFilteredData {
    filteredEvents?: Array<string>;
    filteredFlights?: Array<CORE.FlightId>;
    filteredHotspotTvs?: Array<string>;
    filteredMeasures?: Array<CORE.MeasureId>;
    filteredRegulationOutlook?: Array<string>;
    filteredTvs?: Array<string>;
    flightsExtraData?: Array<FilteredFlightExtraData>;
}
export interface DashboardDataRequest {
    capacityModel?: CORE.CapacityModel;
    convectionSource?: ConvectionSource;
    mapConvectionRequest?: NetworkMapConvectionRequest;
    monitoredPeriod?: CORE.TimePeriod;
    overlappingFilter?: OverlappingFilter;
    version?: number;
}
export interface OverlappingFilter {
    eventsFilters?: Array<Filter>;
    flightsFilters?: Array<Filter>;
    measuresFilters?: Array<Filter>;
    selectedEvents?: Array<string>;
    selectedFlights?: Array<CORE.FlightId>;
    selectedHotspotTvs?: Array<string>;
    selectedMeasures?: Array<CORE.MeasureId>;
    selectedRegOutlookTvs?: Array<string>;
    selectedTvs?: Array<string>;
    tvsFilters?: Array<Filter>;
}
export interface Filter {
    enabled?: boolean;
    filterConfig?: FilterConfig;
    filterLogic?: FilterLogic;
    filteringEntity?: Entity;
}
export enum FilterLogic {
    UNION = 'UNION',
    INTERSECT = 'INTERSECT',
}
export enum Entity {
    TV = 'TV',
    MEASURE = 'MEASURE',
    FLIGHT = 'FLIGHT',
    EVENT = 'EVENT',
}
export interface FilterConfig {
    clazz?: string;
}
export const FILTER_CONFIG_CLAZZ_VALUE = 'eurocontrol.npp.sol49.shared.dto.filter.FilterConfig';
export interface FlightMeasureFilterConfig extends FilterConfig {
    mprOnly?: boolean;
}
export const FLIGHT_MEASURE_FILTER_CONFIG_CLAZZ_VALUE =
    'eurocontrol.npp.sol49.shared.dto.filter.FlightMeasureFilterConfig';
export interface FlightTvFilterConfig extends FilterConfig {
    futureOnly?: boolean;
    marginMs?: number;
    onlyAboveThreshold?: boolean;
    threshold?: CORE.LoadClassification;
    tvOccupancyPeriods?: { [key: string]: Array<CORE.TimePeriod> };
}
export const FLIGHT_TV_FILTER_CONFIG_CLAZZ_VALUE = 'eurocontrol.npp.sol49.shared.dto.filter.FlightTvFilterConfig';
export interface TvFlightFilterConfig extends FilterConfig {
    onlyContributesToOverload?: boolean;
}
export const TV_FLIGHT_FILTER_CONFIG_CLAZZ_VALUE = 'eurocontrol.npp.sol49.shared.dto.filter.TvFlightFilterConfig';
export interface NIATvImpact {
    countType?: CORE.CountType;
    deltaDuration?: number;
    loadAfter?: CORE.LoadClassification;
    loadBefore?: CORE.LoadClassification;
    timeRange?: CORE.TimeRange;
    tvId?: string;
}
export interface DetailedSolutionsRequest extends SolutionsRequest {
    includeNiaImpactedExtra?: boolean;
    solutions?: Array<Solution>;
}
export interface SolutionsRequest {
    capacityModel?: CORE.CapacityModel;
    dataset?: CORE.DatasetDto;
    monitoredPeriod?: CORE.TimePeriod;
    proposedMeasures?: Array<CORE.MeasureDto>;
    tvAndPeriods?: Array<TVTimePeriodSelection>;
}
export interface TVTimePeriodSelection {
    selected?: boolean;
    timePeriod?: CORE.TimePeriod;
    tvId?: string;
}
export interface FlightDeltaDelay {
    deltaDelayMs?: number;
    flightId?: CORE.FlightId;
}
export interface FlightNetworkImprovement {}
export interface FlightNetworkImprovementReply {
    flightDeltaDelays?: Array<FlightDeltaDelay>;
    flightImprovement?: FlightNetworkImprovement;
}
export interface FlightNetworkImprovementRequest {
    datasetReference?: CORE.DatasetReferenceDto;
    flightImprovement?: FlightNetworkImprovement;
}
export interface ForceCtotImprovement extends FlightNetworkImprovement {
    clazz?: string;
    flightId?: CORE.FlightId;
    forceEntryTime?: Date;
    forceEntryType?: CORE.ForceEntryType;
    regulationId?: string;
}
export const FORCE_CTOT_IMPROVEMENT_CLAZZ_VALUE = 'eurocontrol.npp.sol49.shared.dto.ForceCtotImprovement';
export interface GetMonitoredEventsRequest {
    measure?: string;
    refLocation?: CORE.TvRefLocation;
    tv?: string;
}
export interface MeasureCalculatedAutoPeriodReply {
    calculatedPeriod?: CORE.TimePeriod;
}
export interface MeasureCalculatedAutoPeriodRequest {
    datasetReference?: CORE.DatasetReferenceDto;
    regulation?: CORE.RegulationOrMcdmOnlyDto;
}
export interface MeasureNetworkImpactRequest {
    datasetReference?: CORE.DatasetReferenceDto;
    regulation?: CORE.RegulationDto;
}
export interface MonitorAdditionalTrafficVolumeReply {
    tvsAdded?: Array<string>;
}
export interface MonitorAdditionalTrafficVolumeRequest {
    additionalTVs?: Array<string>;
}
export interface MonitoringDataRequest {
    capacityModel?: CORE.CapacityModel;
    convectionSource?: ConvectionSource;
    tvsWithTimePeriod?: Array<TVTimePeriodSelection>;
}
export enum TvSetForecastProgress {
    WIP = 'WIP',
    DONE = 'DONE',
}
export interface PredictedCapacity {
    impacted?: boolean;
    predictedCapacity?: Array<CORE.CapacityValue>;
    tv?: string;
}
export interface PredictedCapacityReply {
    errors?: Array<CORE.ErrorResponse>;
    predictions?: Array<PredictedCapacity>;
}
export interface SolutionsReply {
    errors?: Array<CORE.ErrorResponse>;
    solutions?: Array<Solution>;
}
export interface MonitoredEvent {
    clazz?: string;
    id?: string;
    lastUpdate?: Date;
    title?: string;
    validity?: CORE.TimePeriod;
}
export const MONITORED_EVENT_CLAZZ_VALUE = 'eurocontrol.npp.sol49.shared.dto.events.MonitoredEvent';
export enum NoteEvent_ShareWith {
    TACT = 'TACT',
    POST_OPS = 'POST_OPS',
}
export enum NoteEvent_NoteStatus {
    IN_PREPARATION = 'IN_PREPARATION',
    READY = 'READY',
    RELEASED = 'RELEASED',
    ANALYSED = 'ANALYSED',
    FINALISED = 'FINALISED',
}
export enum NoteEvent_NoteType {
    GEN = 'GEN',
    SE = 'SE',
    CAP = 'CAP',
}
export interface NoteEvent extends MonitoredEvent {
    attention?: string;
    description?: string;
    fmps?: Array<string>;
    internalOnly?: boolean;
    measures?: Array<string>;
    share?: Array<NoteEvent_ShareWith>;
    status?: NoteEvent_NoteStatus;
    tvs?: Array<string>;
    type?: NoteEvent_NoteType;
}
export const NOTE_EVENT_CLAZZ_VALUE = 'eurocontrol.npp.sol49.shared.dto.events.NoteEvent';
export interface WeatherEvent extends MonitoredEvent {
    base64PDF?: string;
    description?: string;
    opsInstructions?: Array<OpsInstruction>;
    refLocs?: Array<CORE.TvRefLocation>;
    type?: string;
}
export const WEATHER_EVENT_CLAZZ_VALUE = 'eurocontrol.npp.sol49.shared.dto.events.WeatherEvent';
export interface OpsInstruction extends MonitoredEvent {
    description?: string;
    name?: string;
    pdf?: Array<number>;
}
export const OPS_INSTRUCTION_CLAZZ_VALUE = 'eurocontrol.npp.sol49.shared.dto.events.OpsInstruction';
export interface NetworkSummaryDto {
    aerodromes?: { [key: string]: RegulatedInfo };
    aerodromesFreeText?: Array<string>;
    airspace?: { [key: string]: RegulatedInfo };
    airspaceFreeText?: Array<string>;
    expectedFlightInfo?: string;
    generalInfo?: string;
    weather?: string;
}
export enum RegulatedInfo_RegulatedInfoType {
    MEASURE = 'MEASURE',
    HOTSPOT = 'HOTSPOT',
}
export interface RegulatedInfo {
    info?: string;
    location?: string;
    selected?: boolean;
    type?: RegulatedInfo_RegulatedInfoType;
}
export interface ATFCMDetail {
    hotspots?: Array<CORE.HotspotDto>;
    info?: string;
    regulations?: Array<CORE.RegulationDto>;
    scenarios?: Array<CORE.RegulationDto>;
}
export interface InitialNetworkPlanDto {
    atfcmDetails?: { [key: string]: ATFCMDetail };
    date?: Date;
    milex?: { [key: string]: Milex };
    networkSummary?: NetworkSummaryDto;
}
export interface Milex {
    edited?: boolean;
    event?: WeatherEvent;
}
export interface AccUnitDto {
    definition?: CORE.Airspace;
}
export interface GetAccUnitsReply {
    accs?: Array<AccUnitDto>;
    ecacElementIds?: Array<string>;
}
export interface GetNatTracksReply {
    natTracks?: Array<CORE.EnRoute>;
}
export interface GetNatTracksRequest {
    day?: Date;
    period?: CORE.TimePeriod;
}
export interface MilitaryZoneDto_MilitaryZoneActivation {
    flightLevelBand?: CORE.FlightLevelBand;
    timePeriod?: CORE.TimePeriod;
}
export interface MilitaryZoneDto {
    activations?: Array<MilitaryZoneDto_MilitaryZoneActivation>;
    airspace?: CORE.Airspace;
    airspaceId?: string;
    amcId?: string;
}
export interface RetrieveMilitaryZonesHighestSequenceRequest {
    day?: Date;
}
export interface RetrieveMilitaryZonesReply {
    militaryZones?: Array<MilitaryZoneDto>;
    sequence?: number;
}
export interface RetrieveMilitaryZonesRequest {
    datasetAndDay?: CORE.DatasetAndDayDto;
    includeAirspaceDefinitions?: boolean;
}
export interface Sol49MapFlightsDataRequestDto extends CORE.MapFlightsDataRequestDto {
    convectionSource?: ConvectionSource;
    flightProfileTrafficType?: CORE.TrafficType;
    monitoringDataVersion?: number;
    retrieveCurrentFlightAirspaceCrossingsWithLoad?: boolean;
}
export const SOL49_MAP_FLIGHTS_DATA_REQUEST_DTO_CLAZZ_VALUE =
    'eurocontrol.npp.sol49.shared.dto.map.Sol49MapFlightsDataRequestDto';
export interface Sol49MapTrafficVolumeListRequest extends CORE.MapTrafficVolumeListRequest {
    dataset?: CORE.DatasetDto;
    ignoreUnknownTvIds?: boolean;
    tvIdList?: Array<string>;
}
export interface Sol49MapFlightsDataReplyDto extends CORE.MapFlightsDataDto {
    mapConvection?: NetworkMapConvection;
}
export interface MonitoringDataReply {
    monitoredTvs?: Array<ExtendedMonitoredTv>;
    tvMeteoAlerts?: { [key: string]: Array<TvMeteoAlert> };
}
export interface DetailedScenarioSolutionsRequest {
    capacityModel?: CORE.CapacityModel;
    dataset?: CORE.DatasetDto;
    impactedTvs?: Array<string>;
    includeNiaImpactedExtra?: boolean;
    relativeTv?: string;
    relativeTvMatchingKind?: CORE.ScenarioTvMatchingKind;
    scenarioId?: string;
    timePeriod?: CORE.TimePeriod;
}
export interface MeasureNetworkImpactReply {
    globalDelayVariation?: number;
    slotList?: CORE.SlotList;
    tvImpacts?: Array<NIATvImpact>;
}
export interface RadRestrictions {
    offLoadingAndOnloadingRadRestrictions?: Array<RadRestriction>;
    offLoadingRadRestrictions?: Array<RadRestriction>;
    onLoadingRadRestrictions?: Array<RadRestriction>;
}
export interface RadRestriction {
    id?: string;
    imageBase64?: string;
    offloadedAreas?: Array<string>;
    onloadedAreas?: Array<string>;
    pointOrAirspaceBlack?: string;
    pointOrAirspaceRed?: string;
    preAgreed?: boolean;
    timeApplicability?: string;
    utilization?: string;
}
export interface DetailedSolutionsReply {
    errors?: Array<CORE.ErrorResponse>;
    solutions?: Array<DetailedSolution>;
}
export interface RadRestrictionsRequest {
    areaPolygon?: Array<CORE.LatLonPoint>;
}
export interface AccDashboardData extends DashboardData {
    configuredAirspaces?: Array<ConfiguredAirspace>;
}
export const ACC_DASHBOARD_DATA_CLAZZ_VALUE = 'eurocontrol.npp.sol49.shared.dto.AccDashboardData';
export interface DashboardData {
    clazz?: string;
    coordinationSummaries?: Array<CoordinationSummary>;
    delaySummary?: DelaySummary;
    flights?: Array<Sol49FlightDto>;
    hotspots?: { [key: string]: Array<CORE.HotspotDto> };
    mapConvection?: NetworkMapConvection;
    monitoredMcdmMeasures?: Array<MonitoredMcdmMeasure>;
    monitoredMeasures?: Array<MonitoredMeasure>;
    monitoredRegulationOutlook?: Array<MonitoredRegulationOutlook>;
    monitoredTvList?: Array<ExtendedMonitoredTv>;
    period?: CORE.TimePeriod;
    tvMeteoAlerts?: { [key: string]: Array<TvMeteoAlert> };
    version?: number;
}
export const DASHBOARD_DATA_CLAZZ_VALUE = 'eurocontrol.npp.sol49.shared.dto.DashboardData';
export interface DashboardDataReply {
    dashboardData?: DashboardData;
    filteredData?: DashboardFilteredData;
    mapConvection?: NetworkMapConvection;
}
export interface NetworkDashboardData extends DashboardData {
    tvSetForecastProgress?: { [key: string]: TvSetForecastProgress };
    tvSetMap?: { [key: string]: string };
    tvSetlastUpdate?: { [key: string]: Date };
}
export const NETWORK_DASHBOARD_DATA_CLAZZ_VALUE = 'eurocontrol.npp.sol49.shared.dto.NetworkDashboardData';
export class Sol49Service {
    baseUrl: string;
    serviceUrl: string = '/services/Sol49Service';
    constructor(baseUrl: string) {
        this.baseUrl = baseUrl;
    }

    public addMonitoredEvent(payload: MonitoredEvent): Promise<CORE.Reply<string>> {
        return this.doSend(this.baseUrl + this.serviceUrl + '/addMonitoredEvent', 'post', payload);
    }
    public createCoordination(payload: CreateCoordinationRequest): Promise<CORE.Reply<string>> {
        return this.doSend(this.baseUrl + this.serviceUrl + '/createCoordination', 'post', payload);
    }
    public deleteMonitoredEvent(payload: string): Promise<CORE.Reply<boolean>> {
        return this.doSend(this.baseUrl + this.serviceUrl + '/deleteMonitoredEvent', 'post', payload);
    }
    public getAccUnits(): Promise<CORE.Reply<GetAccUnitsReply>> {
        return this.doSend(this.baseUrl + this.serviceUrl + '/getAccUnits', 'get');
    }
    public getCoordination(id: string): Promise<CORE.Reply<Coordination>> {
        return this.doSend(this.baseUrl + this.serviceUrl + '/getCoordination/' + id, 'get');
    }
    public getCoordinationChatData(id: string): Promise<CORE.Reply<Array<CORE.ChatMessage>>> {
        return this.doSend(this.baseUrl + this.serviceUrl + '/getCoordinationChatData/' + id, 'get');
    }
    public getCoordinationStatus(id: string): Promise<CORE.Reply<{ [key: string]: { [key: string]: SolutionState } }>> {
        return this.doSend(this.baseUrl + this.serviceUrl + '/getCoordinationStatus/' + id, 'get');
    }
    public getCoordinationVersion(id: string): Promise<CORE.Reply<number>> {
        return this.doSend(this.baseUrl + this.serviceUrl + '/getCoordinationVersion/' + id, 'get');
    }
    public getDashboardData(payload: DashboardDataRequest): Promise<CORE.Reply<DashboardDataReply>> {
        return this.doSend(this.baseUrl + this.serviceUrl + '/getDashboardData', 'post', payload);
    }
    public getDashboardFilteredData(payload: DashboardDataRequest): Promise<CORE.Reply<DashboardFilteredData>> {
        return this.doSend(this.baseUrl + this.serviceUrl + '/getDashboardFilteredData', 'post', payload);
    }
    public getDataVersion(dataset: CORE.DatasetType): Promise<CORE.Reply<number>> {
        return this.doSend(this.baseUrl + this.serviceUrl + '/getDataVersion/' + dataset, 'get');
    }
    public getDetailedScenarioSolution(
        payload: DetailedScenarioSolutionsRequest,
    ): Promise<CORE.Reply<DetailedSolutionsReply>> {
        return this.doSend(this.baseUrl + this.serviceUrl + '/getScenarioSolutionsDetails', 'post', payload);
    }
    public getDetailedSolutions(payload: DetailedSolutionsRequest): Promise<CORE.Reply<DetailedSolutionsReply>> {
        return this.doSend(this.baseUrl + this.serviceUrl + '/getSolutionsDetails', 'post', payload);
    }
    public getFlightListImpactedByConvection(
        payload: GetFlightListImpactedByConvectionRequest,
    ): Promise<CORE.Reply<CORE.FlightListDto>> {
        return this.doSend(this.baseUrl + this.serviceUrl + '/getFlightListImpactedByConvection', 'post', payload);
    }
    public getFlightNetworkImprovement(
        payload: FlightNetworkImprovementRequest,
    ): Promise<CORE.Reply<FlightNetworkImprovementReply>> {
        return this.doSend(this.baseUrl + this.serviceUrl + '/getFlightNetworkImprovement', 'post', payload);
    }
    public getFmpUnits(): Promise<CORE.Reply<Array<CORE.Unit>>> {
        return this.doSend(this.baseUrl + this.serviceUrl + '/getFmpUnits', 'get');
    }
    public getInitialNetworkPlan(date: number): Promise<CORE.Reply<InitialNetworkPlanDto>> {
        return this.doSend(this.baseUrl + this.serviceUrl + '/getInitialNetworkPlan/' + date, 'get');
    }
    public getMapConvection(payload: NetworkMapConvectionRequest): Promise<CORE.Reply<NetworkMapConvection>> {
        return this.doSend(this.baseUrl + this.serviceUrl + '/getNetworkMapConvection', 'post', payload);
    }
    public getMapFlightsData(payload: Sol49MapFlightsDataRequestDto): Promise<CORE.Reply<Sol49MapFlightsDataReplyDto>> {
        return this.doSend(this.baseUrl + this.serviceUrl + '/getMapFlightsData', 'post', payload);
    }
    public getMapTrafficVolumeList(
        payload: Sol49MapTrafficVolumeListRequest,
    ): Promise<CORE.Reply<Array<CORE.MapTrafficVolume>>> {
        return this.doSend(this.baseUrl + this.serviceUrl + '/getMapTrafficVolumeList', 'post', payload);
    }
    public getMeasureCalculatedPeriod(
        payload: MeasureCalculatedAutoPeriodRequest,
    ): Promise<CORE.Reply<MeasureCalculatedAutoPeriodReply>> {
        return this.doSend(this.baseUrl + this.serviceUrl + '/getMeasureCalculatedPeriod', 'post', payload);
    }
    public getMeasureNetworkImpact(
        payload: MeasureNetworkImpactRequest,
    ): Promise<CORE.Reply<MeasureNetworkImpactReply>> {
        return this.doSend(this.baseUrl + this.serviceUrl + '/getMeasureNetworkImpact', 'post', payload);
    }
    public getMilitaryZones(payload: RetrieveMilitaryZonesRequest): Promise<CORE.Reply<RetrieveMilitaryZonesReply>> {
        return this.doSend(this.baseUrl + this.serviceUrl + '/getMilitaryZones', 'post', payload);
    }
    public getMilitaryZonesLastSequence(
        payload: RetrieveMilitaryZonesHighestSequenceRequest,
    ): Promise<CORE.Reply<number>> {
        return this.doSend(this.baseUrl + this.serviceUrl + '/getMilitaryZonesLastSequence', 'post', payload);
    }
    public getMonitoredEvents(payload: GetMonitoredEventsRequest): Promise<CORE.Reply<Array<MonitoredEvent>>> {
        return this.doSend(this.baseUrl + this.serviceUrl + '/getMonitoredEvents', 'post', payload);
    }
    public getMonitoredMcdmMeasures(payload: CORE.TimePeriod): Promise<CORE.Reply<Array<MonitoredMcdmMeasure>>> {
        return this.doSend(this.baseUrl + this.serviceUrl + '/getMonitoredMcdmMeasures', 'post', payload);
    }
    public getMonitoringData(payload: MonitoringDataRequest): Promise<CORE.Reply<MonitoringDataReply>> {
        return this.doSend(this.baseUrl + this.serviceUrl + '/getMonitoringData', 'post', payload);
    }
    public getNatTracks(payload: GetNatTracksRequest): Promise<CORE.Reply<GetNatTracksReply>> {
        return this.doSend(this.baseUrl + this.serviceUrl + '/getNatTracks', 'post', payload);
    }
    public getPredictedCapacity(
        convectionSource: ConvectionSource,
        payload: { [key: string]: Array<TvMeteoAlert> },
    ): Promise<CORE.Reply<PredictedCapacityReply>> {
        return this.doSend(
            this.baseUrl + this.serviceUrl + '/getPredictedCapacity/' + convectionSource,
            'post',
            payload,
        );
    }
    public getRadRestrictionsByPolygon(payload: RadRestrictionsRequest): Promise<CORE.Reply<RadRestrictions>> {
        return this.doSend(this.baseUrl + this.serviceUrl + '/getRadRestrictions', 'post', payload);
    }
    public getSolutions(payload: SolutionsRequest): Promise<CORE.Reply<SolutionsReply>> {
        return this.doSend(this.baseUrl + this.serviceUrl + '/getSolutions', 'post', payload);
    }
    public getTVCounts(payload: string): Promise<CORE.Reply<TrafficVolumeCounts>> {
        return this.doSend(this.baseUrl + this.serviceUrl + '/getTVCounts', 'get', payload);
    }
    public getTvMeteoAlerts(tvId: string, source: ConvectionSource): Promise<CORE.Reply<Array<TvMeteoAlert>>> {
        return this.doSend(this.baseUrl + this.serviceUrl + '/getTvMeteoAlerts/' + tvId + '/' + source, 'get');
    }
    public monitorAdditionalTrafficVolume(
        payload: MonitorAdditionalTrafficVolumeRequest,
    ): Promise<CORE.Reply<MonitorAdditionalTrafficVolumeReply>> {
        return this.doSend(this.baseUrl + this.serviceUrl + '/monitorAdditionalTrafficVolume', 'post', payload);
    }
    public updateCoordination(payload: Coordination): Promise<CORE.Reply<Coordination>> {
        return this.doSend(this.baseUrl + this.serviceUrl + '/updateCoordination', 'post', payload);
    }
    public updateCoordinationStatus(
        payload: UpdateCoordinationStateRequest,
    ): Promise<CORE.Reply<{ [key: string]: { [key: string]: SolutionState } }>> {
        return this.doSend(this.baseUrl + this.serviceUrl + '/updateCoordinationStatus', 'post', payload);
    }
    public updateInitialNetworkPlan(payload: InitialNetworkPlanDto): Promise<CORE.Reply<undefined>> {
        return this.doSend(this.baseUrl + this.serviceUrl + '/updateInitialNetworkPlan', 'post', payload);
    }
    public updateMonitoredEvent(payload: MonitoredEvent): Promise<CORE.Reply<boolean>> {
        return this.doSend(this.baseUrl + this.serviceUrl + '/updateMonitoredEvent', 'post', payload);
    }
    public updatetvSetForecastProgress(
        date: number,
        tvSet: string,
        progress: TvSetForecastProgress,
    ): Promise<CORE.Reply<undefined>> {
        return this.doSend(
            this.baseUrl + this.serviceUrl + '/updatetvSetForecastProgress/' + date + '/' + tvSet + '/' + progress,
            'post',
        );
    }
    private doSend<RequestType, ResponseType>(
        url: string,
        method: 'post' | 'get',
        request?: RequestType,
    ): Promise<ResponseType> {
        const headers = new Headers();
        headers.append('Accept', 'application/json'); // This one is enough for GET requests
        headers.append('Content-Type', 'application/json'); // This one sends body
        return fetch(url, { method: method, headers: headers, body: JSON.stringify(request) }).then(response => {
            if (!response.ok) {
                throw new Error(response.statusText);
            }
            return response.text().then(text => JSON.parse(text));
        });
    }
}
